export type AuthErrorCode =
  | 'invalid_credentials'
  | 'account_locked'
  | 'invalid_invitation'
  | 'expired_invitation'
  | 'weak_password'
  | 'invalid_refresh_token'
  // Refresh rejected because the user's organization is suspended.
  | 'organization_suspended'
  // fix/refresh-rotation-lost-response (security review, finding 3) — the
  // server lost a compare-and-swap race for this token: a genuinely
  // concurrent second request for the SAME token (two tabs, a bootstrap
  // call racing a 401-retry), not a dead/invalid credential. api-client's
  // refresh helpers (see use-auth.ts) retry once on this code and never
  // treat it as session-expired.
  | 'refresh_in_progress'
  | 'invalid_reset_token'
  // Station device pairing: unknown or revoked opaque device token (401).
  | 'invalid_station_token'
  // 403 from SubscriptionAccessGuard: the org's subscription is suspended/canceled.
  | 'subscription_access_blocked'
  | 'invalid_mfa_token'
  | 'rate_limited'
  | 'invalid_input'
  | 'duplicate_active_ticket'
  | 'mfa_email_otp_cooldown'
  | 'mfa_email_otp_expired'
  | 'network_error'
  | 'photo_upload_failed'
  | 'unknown';

export class ApiError extends Error {
  constructor(
    public readonly code: AuthErrorCode,
    message: string,
    public readonly status?: number,
    public readonly details?: unknown,
    /**
     * True when `code` was derived from the HTTP status because the response
     * body carried none (see `mapStatusToCode`). Such a code is a guess — e.g.
     * `invalid_credentials` for ANY code-less 401 — so translators prefer the
     * status over it. Defaults to false: every server-sent code is authoritative.
     */
    public readonly codeIsSynthesized = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown by createTicket when the API returns 409 duplicate_active_ticket. */
export class DuplicateTicketApiError extends ApiError {
  constructor(
    public readonly existingTicketNumber: number,
    message = 'An active ticket for this plate already exists',
  ) {
    super('duplicate_active_ticket', message, 409);
    this.name = 'DuplicateTicketApiError';
  }
}
